import * as React from "react";
import styled from "styled-components";
import ReactDropdown, { DropdownOption } from "../common/ReactDropdown";
import { SingleValue } from "react-select";
import FortnoxInvoiceCreatorClient, {
  FilterValues,
} from "../fortnox/invoice_creator_client";
import { isDateInCurrentOrFutureMonth } from ".";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import DatePicker from "../common/DatePicker";

interface Props {
  filterValues: FilterValues;
  setFilterValues: (filterValues: FilterValues) => void;
  companyTypes: DropdownOption[];
  companies: DropdownOption[];
  client: React.MutableRefObject<FortnoxInvoiceCreatorClient | undefined>;
}

const Wrapper = styled.table.attrs({ className: "standard-form-table" })`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  & > * {
    margin: 0;
  }

  & tr.warning {
    background-color: rgba(255, 200, 0, 0.4);
  }

  & .fetch-projects-error {
    margin: 5px;
    padding: 5px;
    background: rgba(255, 0, 0, 0.1);
    border-radius: 3px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    & button {
      margin: 0;
      padding: 10px;
      background-color: transparent;
      color: #14679e;

      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }

    & span {
      max-width: 200px;
    }
  }
`;

const DateRange = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center; // Align children vertically

  // Remove padding from direct children
  & > * {
    margin: 0;
  }

  // Style for direct input children
  & > input {
    width: 135px;
  }
`;

const FilterForm = ({
  filterValues,
  filterValues: { startDate, endDate, companyType, companyId, projectId },
  setFilterValues,
  companyTypes,
  companies,
  client,
}: Props) => {
  const [projects, setProjects] = React.useState<DropdownOption[] | null>(null);
  const [fetchProjectsError, setFetchProjectsError] = React.useState<
    string | null
  >(null);

  const onCompanyTypeChanged = (value: SingleValue<DropdownOption>) => {
    setFilterValues({ ...filterValues, companyType: value?.value });
  };

  React.useEffect(() => {
    fetchProjects(companyId);
  }, [companyId]);

  const fetchProjects = async (companyId: string | undefined | null) => {
    setFetchProjectsError(null);
    setProjects(null);

    if (companyId == null) {
      setProjects([{ label: "Ej specifierat", value: "-1" }]);
      return;
    }

    if (client.current == undefined) {
      throw new Error("Expected client to be set");
    }

    try {
      const projects = await client.current.getCompanyProjects(companyId);
      projects.splice(0, 0, { label: "Ej specifierat", value: "-1" });
      setProjects(projects);
    } catch (error: Error | any) {
      setFetchProjectsError("Förfrågan misslyckades");
    }
  };

  const onCompanyChanged = (value: SingleValue<DropdownOption>) => {
    setFilterValues({
      ...filterValues,
      companyId: value?.value,
      projectId: undefined,
    });
    setProjects(null);
    setFetchProjectsError(null);
    fetchProjects(value?.value);
  };

  const onProjectChanged = (value: SingleValue<DropdownOption>) => {
    setFilterValues({ ...filterValues, projectId: value?.value });
  };

  return (
    <Wrapper>
      <tbody>
        <tr
          className={classNames(
            isDateInCurrentOrFutureMonth(endDate) && "warning"
          )}
        >
          <td>(*) Datum</td>
          <td>
            <DateRange>
              <DatePicker
                value={startDate}
                onChange={(date) => {
                  let endDate = filterValues.endDate;

                  if (date > endDate) {
                    endDate = date;
                  }

                  setFilterValues({
                    ...filterValues,
                    startDate: date,
                    endDate,
                  });
                }}
              />
              <DatePicker
                value={endDate}
                onChange={(date) => {
                  let startDate = filterValues.startDate;

                  if (startDate > date) {
                    startDate = date;
                  }

                  setFilterValues({
                    ...filterValues,
                    startDate,
                    endDate: date,
                  });
                }}
              />
            </DateRange>
          </td>
          <td>
            {isDateInCurrentOrFutureMonth(endDate) && (
              <span>Obs: slutmånaden är inte slutförd än.</span>
            )}
          </td>
        </tr>
        <tr>
          <td>Företagstyp</td>
          <td>
            <ReactDropdown
              options={companyTypes}
              placeholder="Välj företagstyp"
              slim={true}
              value={companyType}
              onChange={onCompanyTypeChanged}
            />
          </td>
        </tr>
        <tr>
          <td>Företag</td>
          <td>
            <ReactDropdown
              options={companies}
              placeholder="Välj företag"
              isClearable
              slim
              value={companyId}
              onChange={onCompanyChanged}
            />
          </td>
        </tr>
        <tr>
          <td>Projekt</td>
          <td>
            <ReactDropdown
              options={projects == null ? [] : projects}
              placeholder="Välj projekt"
              isClearable
              isLoading={projects == null || fetchProjectsError != null}
              isDisabled={fetchProjectsError != null}
              slim
              value={projectId}
              onChange={onProjectChanged}
            />

            {fetchProjectsError && (
              <div className="fetch-projects-error">
                <span className="error">{fetchProjectsError}</span>
                <button
                  className="button"
                  onClick={() => {
                    fetchProjects(companyId);
                  }}
                  title="Försök igen"
                >
                  <FontAwesomeIcon icon={faRefresh} />
                </button>
              </div>
            )}
          </td>
        </tr>
      </tbody>
    </Wrapper>
  );
};

export default FilterForm;
